/**
 * SearchForm
 * スマホ時のみドロワー表示される検索フォーム機能
 *
 * @usage
 * HTML: <button class="js-searchform-trigger">検索</button>
 * JS: new SearchForm();
 */
export default class SearchForm {
  constructor(options = {}) {
    this.options = {
      trigger: '.js-searchform-trigger',
      openClass: 'is-searchform-open',
      ...options
    };

    this.trigger = document.querySelector(this.options.trigger);
    this.isOpen = false;

    if (this.trigger) {
      this.init();
    } else {
      console.warn('SearchForm: trigger element not found');
    }
  }

  init() {
    this.bindEvents();
    this.setupAccessibility();
  }

  bindEvents() {
    // 検索トリガーボタンのクリック
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });

    // ハンバーガーメニューを監視して閉じる
    document.addEventListener('hamburger:opened', () => {
      if (this.isOpen) {
        this.close();
        // ハンバーガーメニューが開いた後、スクロールロックを再実行
        setTimeout(() => {
          document.body.style.overflow = 'hidden';
        }, 325);
      }
    });

    // ハンバーガーメニューボタンのクリックで閉じる
    const hamburgerTrigger = document.querySelector('.js-hamburger');
    if (hamburgerTrigger) {
      hamburgerTrigger.addEventListener('click', () => {
        if (this.isOpen) {
          this.close();
          // ハンバーガーメニューが開く際のスクロールロック再実行
          setTimeout(() => {
            document.body.style.overflow = 'hidden';
          }, 350);
        }
      });
    }

    // スクロール時に閉じる
    let scrollTimer = null;
    window.addEventListener('scroll', () => {
      if (this.isOpen) {
        // スクロール中は一旦タイマーをクリア
        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }
        // 少し遅延させて閉じる（連続スクロール時の過剰な処理を防ぐ）
        scrollTimer = setTimeout(() => {
          this.close();
        }, 50);
      }
    });

    // リサイズ時の処理（PCサイズになったら閉じる）
    window.addEventListener('resize', () => {
      if (this.isOpen && window.innerWidth >= 768) {
        this.close();
      }
    });
  }

  setupAccessibility() {
    // ARIA属性の設定
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', 'search-drawer');
    
    if (!this.trigger.getAttribute('aria-label')) {
      this.trigger.setAttribute('aria-label', '検索フォームを開く');
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;

    // bodyにオープンクラスを追加
    document.body.classList.add(this.options.openClass);

    // ボディスクロールをロック
    this.lockBodyScroll();

    // ARIA属性の更新
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.setAttribute('aria-label', '検索フォームを閉じる');

    // カスタムイベントの発火
    document.dispatchEvent(new CustomEvent('searchform:opened', {
      detail: { searchForm: this }
    }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // bodyからクラスを削除
    document.body.classList.remove(this.options.openClass);

    // ボディスクロールのロック解除
    this.unlockBodyScroll();

    // ARIA属性の更新
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', '検索フォームを開く');

    // カスタムイベントの発火
    document.dispatchEvent(new CustomEvent('searchform:closed', {
      detail: { searchForm: this }
    }));
  }

  lockBodyScroll() {
    // ボディのスクロールを無効化
    document.body.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    // ボディのスクロールを復元
    document.body.style.removeProperty('overflow');
  }

  destroy() {
    // クラスとARIA属性の削除
    this.close();
    this.trigger.removeAttribute('aria-expanded');
    this.trigger.removeAttribute('aria-controls');

    // bodyからクラスを完全削除
    document.body.classList.remove(this.options.openClass);
  }
}